import React, { useRef } from "react";
import { X, RotateCcw, ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from "lucide-react";
import useDialogFocus from "@/hooks/useDialogFocus";
import { L, LANG_NAMES } from "@/lib/i18n";

/**
 * Language pill selector — top-bar chrome. Renders nothing when the project
 * has 0-1 supported languages.
 */
export function LanguageSwitcher({ current, supported, onChange }) {
  if (!supported || supported.length <= 1) return null;
  return (
    <div className="pill-nav inline-flex items-center gap-1 px-1 py-1 rounded-full" data-testid="lang-switcher">
      {supported.map(l => (
        <button key={l} onClick={() => onChange(l)} data-testid={`lang-btn-${l}`}
          className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full transition ${current === l ? "bg-white text-zinc-900" : "text-white/70 hover:text-white"}`}>
          {LANG_NAMES[l] || l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * Direction icon for transition CTAs. Flips L↔R under RTL so the arrow always
 * points to where the swipe will take the viewer.
 */
export function DirIcon({ dir }) {
  const isRTL = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  if (dir === "left") return isRTL ? <ArrowLeft className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />;
  if (dir === "right") return isRTL ? <ArrowRight className="h-3.5 w-3.5" /> : <ArrowLeft className="h-3.5 w-3.5" />;
  if (dir === "up") return <ArrowUp className="h-3.5 w-3.5" />;
  if (dir === "down") return <ArrowDown className="h-3.5 w-3.5" />;
  return null;
}

/** Cinematic loading splash. */
export function Loading({ lang }) {
  return (
    <div className="viewer-shell h-screen w-screen flex flex-col items-center justify-center" data-testid="viewer-loading">
      <div className="h-2.5 w-2.5 rounded-full bg-[#C4A47C] animate-pulse" />
      <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-white/60">{L(lang, "loading")}</div>
    </div>
  );
}

/** Fallback shown when the project is unpublished, deleted, or the fetch failed. */
export function Unavailable({ lang, customMsg }) {
  return (
    <div className="viewer-shell h-screen w-screen flex items-center justify-center p-8 text-center" data-testid="viewer-unavailable">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{L(lang, "unavailable_title")}</div>
        <div className="font-display text-2xl text-white mt-3 max-w-md">{customMsg || L(lang, "unavailable_message")}</div>
      </div>
    </div>
  );
}

/**
 * Lightweight modal shown when a zone with `click_action='show_info'` is
 * activated. Designed to feel like an iOS share sheet: glass panel, accent-
 * color title bar, dismiss-on-backdrop.
 */
export function ZoneInfoPanel({ title, description, imageUrl, onClose, lang }) {
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, onClose);
  return (
    <div
      className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in"
      onClick={onClose}
      data-testid="zone-info-panel"
    >
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title || L(lang, "info")} tabIndex={-1} className="w-full max-h-[85dvh] overflow-auto max-w-md glass-panel rounded-2xl" onClick={(e) => e.stopPropagation()}>
        {imageUrl && (
          <img src={imageUrl} alt="" className="w-full h-44 object-cover" data-testid="zone-info-image" draggable={false} />
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "info")}</div>
              <div className="font-display text-2xl text-white tracking-tight mt-1" data-testid="zone-info-title">{title}</div>
            </div>
            <button onClick={onClose} className="text-white/65 hover:text-white shrink-0" aria-label={L(lang, "close")} data-testid="zone-info-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          {description && (
            <div className="mt-4 text-sm text-white/80 leading-relaxed whitespace-pre-line" data-testid="zone-info-description">
              {description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Shown when the tablet is held in portrait — asks the viewer to rotate. */
export function RotateScreen({ project, lang }) {
  return (
    <div className="viewer-shell h-screen w-screen flex flex-col items-center justify-center p-8 text-center" data-testid="viewer-rotate-screen">
      <div className="h-2.5 w-2.5 rounded-full bg-[#C4A47C]" />
      <div className="font-display text-2xl text-white mt-4">{project?.name}</div>
      <div className="mt-6 inline-flex items-center gap-3 text-white/70 text-sm">
        <RotateCcw className="h-5 w-5 animate-pulse" />
        {L(lang, "rotate_message")}
      </div>
    </div>
  );
}
